import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

export const ROUTING_ERROR_CODES = new Set([
  'DECORATOR_ROUTING_REQUIRED',
  'DECORATOR_ROUTING_AMBIGUOUS',
]);

export const getDecorationRoutingError = (error) => {
  const data = error?.response?.data;
  if (!ROUTING_ERROR_CODES.has(data?.code)) return null;
  const issues = Array.isArray(data?.issues)
    ? data.issues
    : (data?.details && typeof data.details === 'object' ? [data.details] : []);
  return { issues, legacy: issues.length === 0 };
};

const Issue = ({ issue, onOpenProduct }) => {
  const productName = issue?.productName || 'Product with incomplete decoration settings';
  const decorationName = issue?.decorationName || 'Decoration';
  const location = issue?.decorationLocation || null;
  const ambiguous = issue?.reason === 'conflicting-routes';
  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        {issue?.productImage ? <Avatar variant="rounded" src={issue.productImage} alt="" sx={{ width: 64, height: 64 }} /> : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{productName}</Typography>
          {(issue?.productSku || issue?.productColor) ? (
            <Typography variant="body2" color="text.secondary">
              {[issue.productSku ? `SKU ${issue.productSku}` : null, issue.productColor].filter(Boolean).join(' • ')}
            </Typography>
          ) : null}
          <Typography variant="body1" sx={{ mt: 1 }}>
            {[decorationName, location].filter(Boolean).join(' — ')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {ambiguous
              ? 'Review the decorator and production department because this decoration has conflicting routing.'
              : 'Select a decorator or production department for this decoration.'}
          </Typography>
          {onOpenProduct && issue?.orderItemId != null ? (
            <Button size="small" sx={{ mt: 1 }} onClick={() => onOpenProduct(issue.orderItemId, issue.decorationId)}>
              Open Product in Cart
            </Button>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
};

const DecorationRoutingErrorDialog = ({ open, issues = [], onClose, onOpenCart, onOpenProduct }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="decoration-routing-dialog-title">
    <DialogTitle id="decoration-routing-dialog-title">Decoration Details Need Attention</DialogTitle>
    <DialogContent dividers>
      <Typography sx={{ mb: 1 }}>
        We could not regenerate the Sales Order because some products do not have complete decoration routing. Update the products below and try again.
      </Typography>
      {issues.length ? issues.map((issue, index) => (
        <React.Fragment key={`${issue.orderItemId ?? 'item'}:${issue.decorationId ?? issue.decorationLocation ?? index}`}>
          {index > 0 ? <Divider /> : null}
          <Issue issue={issue} onOpenProduct={onOpenProduct} />
        </React.Fragment>
      )) : (
        <Typography sx={{ py: 2 }}>
          One product has incomplete decoration settings. Open the cart and review its decorator assignment before regenerating.
        </Typography>
      )}
    </DialogContent>
    <DialogActions>
      {onOpenCart ? <Button onClick={onOpenCart}>Open Cart</Button> : null}
      <Button variant="contained" onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

export default DecorationRoutingErrorDialog;
